import { MesfModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Grid2, TextField, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GetRowIdParams,
	RowClassParams,
	RowClickedEvent,
} from "ag-grid-community";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { WorkOrder } from "../../models/CampaignData";
import { useChangeWorkOrder } from "../../hooks/useChangeWorkOrder";
import { getWorkOrders } from "../../repositories/MillSetupRepository";

interface ChangeWorkOrderModalProps {
	open: boolean;
	currentWorkOrderName: string;
	onClose: () => void;
	onSuccess: () => void;
	onError: (message: string) => void;
}

interface FormValues {
	workOrderName: string;
	workOrderId: number | null;
}

export const ChangeWorkOrderModal = ({
	open,
	currentWorkOrderName,
	onClose,
	onSuccess,
	onError,
}: ChangeWorkOrderModalProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedWOName, setSelectedWOName] = useState<string | null>(null);
	const gridRef = useRef<AgGridReact<WorkOrder>>(null);

	const { control, handleSubmit, setValue, reset, watch } = useForm<FormValues>(
		{
			defaultValues: {
				workOrderName: "",
				workOrderId: null,
			},
		},
	);

	const currentFormValue = watch("workOrderName");
	const [userHasTyped, setUserHasTyped] = useState(false);

	useEffect(() => {
		if (open) {
			reset({ workOrderName: currentWorkOrderName, workOrderId: null });
			setSelectedWOName(null);
			setUserHasTyped(false);
		}
	}, [open, currentWorkOrderName, reset]);

	const {
		data: workOrders = [],
		isLoading: isLoadingWorkOrders,
		error: workOrdersError,
	} = useQuery({
		queryKey: ["workOrders"],
		queryFn: () => getWorkOrders(),
		enabled: open,
	});

	// Sort so the current WO is always at the top
	const sortedWorkOrders = useMemo(() => {
		if (!currentWorkOrderName) return workOrders;
		const current = workOrders.filter(
			(wo) => String(wo.Name) === currentWorkOrderName,
		);
		const rest = workOrders.filter(
			(wo) => String(wo.Name) !== currentWorkOrderName,
		);
		return [...current, ...rest];
	}, [workOrders, currentWorkOrderName]);

	// Client-side filtering — only filter after user actively types (not pre-fill)
	const filteredWorkOrders = useMemo(() => {
		if (!userHasTyped || !currentFormValue?.trim()) return sortedWorkOrders;
		const s = currentFormValue.toLowerCase();
		return sortedWorkOrders.filter(
			(wo) =>
				String(wo.Name ?? "")
					.toLowerCase()
					.includes(s) ||
				String(wo.ERPProduct ?? "")
					.toLowerCase()
					.includes(s) ||
				String(wo.ScheduledDateString ?? "")
					.toLowerCase()
					.includes(s) ||
				String(wo.ScheduledTons ?? "").includes(s) ||
				String(wo.ScheduledBillets ?? "").includes(s),
		);
	}, [sortedWorkOrders, currentFormValue]);

	const columnDefs = useMemo<ColDef<WorkOrder>[]>(
		() => [
			{
				field: "ScheduledDateString",
				headerName: "Scheduled Date",
				flex: 1.2,
				valueGetter: (params) =>
					dxtToLocalServerTime(
						`${params.data?.ScheduledDateString}`,
						"yyyy-MM-dd HH:mm:ss",
					) || "",
			},
			{ field: "Name", headerName: "Name", flex: 1 },
			{ field: "ERPProduct", headerName: "Product", flex: 1 },
			{
				field: "ScheduledTons",
				headerName: "Tons",
				width: 90,
				valueFormatter: (params) =>
					params.value != null ? Number(params.value).toFixed(2) : "",
			},
			{
				field: "ScheduledBillets",
				headerName: "Billets",
				width: 80,
			},
		],
		[],
	);

	const { changeWorkOrder } = useChangeWorkOrder({
		onSuccess: () => {
			setIsSubmitting(false);
			onSuccess();
		},
		onError: (err) => {
			setIsSubmitting(false);
			onError(err.message);
		},
	});

	const onSubmit = (data: FormValues) => {
		if (!data.workOrderName.trim()) return;
		setIsSubmitting(true);
		changeWorkOrder({
			workOrderId: data.workOrderId,
			workOrderName: data.workOrderName.trim(),
		});
	};

	const handleRowClick = useCallback(
		(event: RowClickedEvent<WorkOrder>) => {
			if (event.data) {
				setValue("workOrderName", String(event.data.Name));
				setValue("workOrderId", event.data.Id);
				setSelectedWOName(String(event.data.Name));
			}
		},
		[setValue],
	);

	const getRowStyle = useCallback(
		(params: RowClassParams<WorkOrder>) => {
			if (!params.data) return undefined;
			const rowName = String(params.data.Name);
			if (rowName === currentFormValue || rowName === selectedWOName) {
				return { backgroundColor: "#d0e8ff" };
			}
			return undefined;
		},
		[currentFormValue, selectedWOName],
	);

	const getRowId = useCallback(
		(params: GetRowIdParams<WorkOrder>) => String(params.data.Id),
		[],
	);

	// Force grid to repaint rows when selection changes
	useEffect(() => {
		gridRef.current?.api?.redrawRows();
	}, [currentFormValue, selectedWOName]);

	return (
		<MesfModal
			id="modal-change-work-order"
			title="Change Work Order"
			open={open}
			handleClose={onClose}
			maxWidth="md"
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<MesfModal.Content style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="workOrderName"
								control={control}
								rules={{ required: "Work order name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label="Work Order"
										value={field.value}
										onChange={(e) => {
											field.onChange(e);
											setValue("workOrderId", null);
											setSelectedWOName(null);
											setUserHasTyped(true);
										}}
										fullWidth
										size="small"
										autoFocus
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>

						<Grid2 size={{ xs: 12 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
								Work Orders
							</Typography>
							<Typography
								variant="caption"
								sx={{ mb: 0.5, display: "block", color: "#888" }}
							>
								{workOrdersError
									? `Error: ${(workOrdersError as Error).message}`
									: "Click a row to select"}
							</Typography>
							<Box sx={{ height: 250, position: "relative" }}>
								{isLoadingWorkOrders && (
									<Box
										sx={{
											position: "absolute",
											inset: 0,
											zIndex: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: "rgba(255,255,255,0.7)",
										}}
									>
										<CenteredLazyLoading />
									</Box>
								)}
								<AgGridReact<WorkOrder>
									ref={gridRef}
									rowData={filteredWorkOrders}
									columnDefs={columnDefs}
									defaultColDef={globalDefaultColDef}
									rowHeight={32}
									headerHeight={32}
									onRowClicked={handleRowClick}
									getRowStyle={getRowStyle}
									getRowId={getRowId}
									suppressCellFocus
									suppressContextMenu
								/>
							</Box>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<FooterModalButtons
						isSubmitLoading={isSubmitting}
						onHide={() => onClose()}
						buttonLabel="Accept"
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
